/**
 * Conversational API service for interacting with the Adya conversational orchestrator
 * This replaces the legacy workflowApi and connects to the production conversational system
 */

const DEFAULT_API_BASE_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000'
const API_BASE_URL = process.env.REACT_APP_API_URL || DEFAULT_API_BASE_URL

// Request/Response interfaces for conversational API
export interface ConversationRequest {
  message: string;
  project_id: string;
  conversation_history?: ConversationMessage[];
}

export interface ConversationMessage {
  type: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
}

export interface ConversationResponse {
  success: boolean;
  response: string;
  mode: 'initial_intake' | 'proposal_refinement' | 'question_answering' | 'general_chat';
  project_id: string;
  next_actions: string[];
  workflow_result?: {
    success: boolean;
    status: string;
    stages_completed: number;
    results?: any;
  };
  metadata: {
    timestamp: string;
    has_history: boolean;
    message_count: number;
    memory_context?: any;
  };
}

export interface UploadResponse {
  success: boolean;
  message: string;
  files_uploaded: number;
  files: Array<{
    original_filename: string;
    stored_filename: string;
    stored_path: string;
    size: number;
  }>;
  project_id: string;
  conversation_result: ConversationResponse;
}

export interface ProjectStatus {
  project_id: string;
  conversation_history: ConversationMessage[];
  workflow_status?: {
    status: string;
    stages_completed: number;
    current_stage?: string;
  };
  memory_context?: {
    memory_count: number;
    insight_count?: number;
    cross_agent_insights?: any[];
    has_proposal?: boolean;
  };
}

export interface ProjectDocument {
  id: string;
  original_filename: string;
  stored_filename: string;
  file_size: number;
  content_type: string | null;
  uploaded_at: string | null;
}

export interface DocumentsResponse {
  success: boolean;
  project_id: string;
  documents: ProjectDocument[];
  total: number;
}

export interface WebSocketMessage {
  type: 'stage_update' | 'workflow_complete' | 'error' | 'connection_confirmed';
  project_id: string;
  stage?: string;
  status?: string;
  results?: any;
  error?: string;
  timestamp: number;
  data?: any;
}

export class ConversationalApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public response?: any
  ) {
    super(message)
    this.name = 'ConversationalApiError'
  }
}

class ConversationalApiService {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`

    // Get access token from localStorage
    const accessToken = localStorage.getItem('access_token')
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options.headers as Record<string, string>,
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new ConversationalApiError(
          errorData.detail || `HTTP ${response.status}: ${response.statusText}`,
          response.status,
          errorData
        )
      }

      return await response.json()
    } catch (error) {
      if (error instanceof ConversationalApiError) {
        throw error
      }

      throw new ConversationalApiError(
        `Network error: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    }
  }

  /**
   * Send a conversational message to the orchestrator
   */
  async sendMessage(
    message: string,
    projectId: string,
    conversationHistory?: ConversationMessage[]
  ): Promise<ConversationResponse> {
    const request: ConversationRequest = {
      message,
      project_id: projectId,
      conversation_history: conversationHistory
    };

    return this.request<ConversationResponse>('/api/conversation', {
      method: 'POST',
      body: JSON.stringify(request)
    })
  }

  /**
   * Upload one or multiple files and process through conversational orchestrator
   */
  async uploadFile(files: File | File[], projectId?: string): Promise<UploadResponse> {
    const fileArray = Array.isArray(files) ? files : [files]
    console.log('🔥 conversationalApi.uploadFile called with:', fileArray.length, 'file(s), projectId:', projectId)
    console.log('🔥 API_BASE_URL:', API_BASE_URL)

    const formData = new FormData()
    fileArray.forEach(file => {
      formData.append('files', file)
    })
    if (projectId) {
      formData.append('project_id', projectId)
    }

    // Get access token from localStorage
    const accessToken = localStorage.getItem('access_token')
    if (!accessToken) {
      throw new ConversationalApiError('Not authenticated', 401)
    }

    console.log('🔥 Making fetch to:', `${API_BASE_URL}/api/upload`)
    try {
      const response = await fetch(`${API_BASE_URL}/api/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`
        },
        body: formData,
      });
      console.log('🔥 Fetch response status:', response.status, response.statusText)

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new ConversationalApiError(
          errorData.detail || `Upload failed: ${response.statusText}`,
          response.status,
          errorData
        )
      }

      return await response.json()
    } catch (fetchError) {
      console.error('🔥 FETCH ERROR:', fetchError)
      throw fetchError
    }
  }

  /**
   * Get project status and conversation history
   */
  async getProjectStatus(projectId: string): Promise<ProjectStatus> {
    return this.request<ProjectStatus>(`/api/project/${projectId}/status`)
  }

  /**
   * Get generated proposal markdown
   */
  async getProposal(projectId: string): Promise<string> {
    const url = `${API_BASE_URL}/api/project/${projectId}/proposal`

    // Get access token from localStorage
    const accessToken = localStorage.getItem('access_token')
    const headers: Record<string, string> = {}
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    try {
      const response = await fetch(url, { headers })

      if (!response.ok) {
        throw new ConversationalApiError(
          `Failed to fetch proposal: ${response.statusText}`,
          response.status
        )
      }

      return await response.text()
    } catch (error) {
      if (error instanceof ConversationalApiError) {
        throw error
      }

      throw new ConversationalApiError(
        `Network error: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    }
  }

  /**
   * Get all documents for a project
   */
  async getDocuments(projectId: string): Promise<DocumentsResponse> {
    try {
      return await this.request<DocumentsResponse>(`/api/project/${projectId}/documents`)
    } catch (error) {
      if (error instanceof ConversationalApiError) {
        throw error
      }

      throw new ConversationalApiError(
        `Failed to retrieve documents: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    }
  }

  /**
   * Create WebSocket connection for real-time updates
   */
  createWebSocket(
    projectId: string,
    onMessage: (message: WebSocketMessage) => void,
    onError?: (error: Event) => void,
    onClose?: (event: CloseEvent) => void
  ): WebSocket {
    const wsUrl = `${API_BASE_URL.replace('http', 'ws')}/ws/${projectId}`
    const ws = new WebSocket(wsUrl)

    ws.onopen = () => {
      console.log(`🔌 WebSocket connected for project ${projectId}`)
    }

    ws.onmessage = (event) => {
      try {
        const message: WebSocketMessage = JSON.parse(event.data)
        onMessage(message)
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error)
      }
    }

    ws.onerror = (error) => {
      console.error('WebSocket error:', error)
      if (onError) onError(error)
    }

    ws.onclose = (event) => {
      console.log(`🔌 WebSocket disconnected for project ${projectId}`)
      if (onClose) onClose(event)
    }

    return ws
  }

  /**
   * Health check endpoint
   */
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    return this.request<{ status: string; timestamp: string }>('/api/health')
  }

  /**
   * Get the base URL for API
   */
  getBaseUrl(): string {
    return API_BASE_URL
  }
}

// Export singleton instance
export const conversationalApi = new ConversationalApiService()

// Legacy compatibility - gradually migrate from workflowApi to conversationalApi
export const workflowApi = {
  // Map old methods to new conversational API
  async startWorkflow(conversation: string, projectId?: string) {
    const id = projectId || `project_${Date.now()}`
    const response = await conversationalApi.sendMessage(conversation, id)
    
    return {
      project_id: response.project_id,
      status: response.workflow_result?.status || 'completed',
      message: response.response,
    }
  },

  async uploadAndStartWorkflow(file: File, projectId?: string) {
    const response = await conversationalApi.uploadFile(file, projectId)
    
    return {
      project_id: response.project_id,
      status: response.conversation_result.workflow_result?.status || 'completed',
      message: response.message,
      files: response.files,
    }
  },

  async getWorkflowStatus(projectId: string) {
    const status = await conversationalApi.getProjectStatus(projectId)
    
    return {
      status: status.workflow_status?.status || 'completed',
      results: status,
    }
  },

  async getProposal(projectId: string) {
    return conversationalApi.getProposal(projectId)
  },
}

export default conversationalApi
